import { Person as PersonIcon } from "@remo-co/ui-core/src/icons/Person";
import { ChangeEvent, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useDebounce from "modules/common/hooks/useDebounce";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Card } from "@remo-co/ui-core/src/components/Card";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { InputAdornment } from "@remo-co/ui-core/src/components/InputAdornment";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Search } from "@remo-co/ui-core/src/icons/Search";
import { useI18n } from "i18n";
import { useNeedHelp } from "modules/helpChat";
import { selectCompanyId } from "modules/company/redux/selectors";
import {
  selectEnableAgencyWhiteLabelling,
  selectIsAgencyWhitelabellingSettingsLoaded,
} from "modules/settings/redux/selectors";
import { useEventDiscovery } from "../../hooks/useEventDiscovery";
import EventList from "../../eventList/EventList";
import { DiscoveryEmptyState } from "../DiscoveryEmptyState";
import { useStyles } from "./styles";

export const EventDiscovery = () => {
  const [query, setQuery] = useState("");
  const { callApi } = useNeedHelp();
  const queryDebounced = useDebounce(query, 300);
  const { events } = useEventDiscovery(queryDebounced);
  const companyId = useSelector(selectCompanyId);
  const isAgencyWhitelabellingSettingsLoaded = useSelector(
    selectIsAgencyWhitelabellingSettingsLoaded,
  );
  const enableAgencyWhiteLabellingGlobal = useSelector(
    selectEnableAgencyWhiteLabelling,
  );
  const { t } = useI18n(["event", "menu"]);
  const styles = useStyles();

  const handleQueryUpdate = (evt: ChangeEvent<HTMLInputElement>) => {
    setQuery(evt.target.value);
  };

  const eventDiscoveryLabels = useMemo(() => {
    if (
      !isAgencyWhitelabellingSettingsLoaded ||
      enableAgencyWhiteLabellingGlobal
    ) {
      return [
        t("event:want.to.see.more.events"),
        t("event:browse.through.events"),
      ];
    }

    // if whitelabel settings are loaded and agency white labelling is disabled
    return [
      companyId ? t("event:remo.in.action") : t("event:looking.attend.event"),
      t("event:browse.through.events.remo"),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    enableAgencyWhiteLabellingGlobal,
    isAgencyWhitelabellingSettingsLoaded,
  ]);

  return (
    <Container top="xl" bottom="lg">
      <Card className={styles.card} innerClassName={styles.cardInner}>
        <Container className={styles.header}>
          <span>
            <Typography variant="h3" className={styles.title}>
              <PersonIcon className={styles.icon} color="primary" />
              {eventDiscoveryLabels[0]}
            </Typography>
            <Typography variant="h5" className={styles.subtitle}>
              {eventDiscoveryLabels[1]}
            </Typography>
            {isAgencyWhitelabellingSettingsLoaded &&
              !enableAgencyWhiteLabellingGlobal && (
                <Container className={styles.contactUs}>
                  <Typography variant="body1" className={styles.subtitle}>
                    {t("event:cannot.find.event.support")}
                  </Typography>
                  <Button
                    variant="text"
                    color="blue"
                    size="md"
                    aria-label={t("event:contact.us")}
                    onClick={() => callApi("showNewMessage")}
                    contentClassName={styles.contactUsButton}
                  >
                    {t("event:contact.us")}
                  </Button>
                </Container>
              )}
          </span>
          <Container className={styles.search}>
            <Input
              inputEndAdornment={
                <InputAdornment
                  className={styles.searchInputAdornment}
                  position="start"
                >
                  <Search />
                </InputAdornment>
              }
              placeholder={t("event:search.for.events")}
              onChange={handleQueryUpdate}
              fullWidth
              value={query}
              size="sm"
              data-testid="chat-search"
              className={styles.inputClass}
            />
          </Container>
        </Container>
        {events && events.length > 0 && <EventList events={events} />}
        {events && events.length === 0 && <DiscoveryEmptyState />}
      </Card>
    </Container>
  );
};
